<script setup>
  import { computed, ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { Minus, Plus, Info, TriangleAlert } from "lucide-vue-next";

  import { Label } from "@/components/ui/label";
  import { Button } from "@/components/ui/button";

  import { useContractsStore } from "@stores/contracts";
  import { useSurveyCreditsStore } from "@stores/survey/setup/surveyCredits";
  import { creditsLabel } from "@utils/credits/creditRules";

  const contractsStore = useContractsStore();
  const creditsStore = useSurveyCreditsStore();

  const {
    currentSurveyId,
    currentContractId,
    currentCreditsMax,
    currentCreditsAuto,
    currentCreditsManual,
    hasManualOverride,
    stepperIncrement,
    ruleLabel,
    accessibleRespondents,
    creditEffective,
    consumedAutoEffective,
    minCreditsMax,
    isAtConsumedFloor,
    nextCreditsMax,
    isContractExhausted,
  } = storeToRefs(creditsStore);
  const { incrementCreditsMax, decrementCreditsMax } = creditsStore;

  const selectedContract = ref(null);
  watch(
    currentContractId,
    async (id) => {
      if (!id) {
        selectedContract.value = null;
        return;
      }
      try {
        selectedContract.value = await contractsStore.fetchOne(id);
      } catch {
        selectedContract.value = null;
      }
    },
    { immediate: true },
  );

  const remainingOnContract = computed(() => selectedContract.value?.credits_remaining ?? Infinity);

  const isSelfOnContract = computed(() =>
    (selectedContract.value?.surveys || []).some((s) => s.id === currentSurveyId.value),
  );
  const selfContribution = computed(() => (isSelfOnContract.value ? creditEffective.value : 0));
  const availableOnContract = computed(() =>
    remainingOnContract.value === Infinity
      ? Infinity
      : remainingOnContract.value + selfContribution.value,
  );

  const exceedsContract = computed(() => nextCreditsMax.value > availableOnContract.value);
</script>

<template>
  <div
    v-if="currentContractId !== null"
    class="space-y-3">
    <Label>Credits allocated</Label>

    <!-- Exhausted contract: show the CSM message exclusively, hide stepper and other warnings. -->
    <div
      v-if="isContractExhausted"
      class="rounded-md bg-status-warning-soft border border-status-warning-border px-3 py-2 text-xs text-status-warning-strong flex items-start gap-2">
      <TriangleAlert class="h-4 w-4 flex-shrink-0 mt-0.5" />
      <div>
        <p class="font-medium">No credits remaining on this contract</p>
        <p>Contact your CSM to allocate additional credits before creating a survey on this contract.</p>
      </div>
    </div>

    <!-- Manual override active: replace the stepper with an informational message. -->
    <div
      v-else-if="hasManualOverride"
      class="rounded-md bg-status-warning-soft border border-status-warning-border px-3 py-2 text-xs text-status-warning-strong flex items-start gap-2">
      <TriangleAlert class="h-4 w-4 flex-shrink-0 mt-0.5" />
      <div>
        <p class="font-medium">Manual override active</p>
        <p>
          Set to {{ currentCreditsManual }} {{ creditsLabel(currentCreditsManual) }} by your CSM. Automatic value would be {{ currentCreditsAuto }} {{ creditsLabel(currentCreditsAuto) }}.
        </p>
      </div>
    </div>

    <template v-else>
      <div class="flex items-center gap-3">
        <Button
          variant="outline"
          size="icon"
          :disabled="currentCreditsMax - stepperIncrement < minCreditsMax"
          @click="decrementCreditsMax">
          <Minus class="h-4 w-4" />
        </Button>

        <span class="font-semibold text-xl tabular-nums w-10 text-center">
          {{ currentCreditsMax }}
        </span>

        <Button
          variant="outline"
          size="icon"
          :disabled="exceedsContract"
          @click="incrementCreditsMax">
          <Plus class="h-4 w-4" />
        </Button>
      </div>

      <div class="rounded-md bg-status-info-soft border border-status-info-border px-3 py-2 text-xs text-status-info-strong flex items-start gap-2">
        <Info class="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          <p class="font-medium">{{ ruleLabel }}</p>
          <p>
            With {{ currentCreditsMax }} {{ creditsLabel(currentCreditsMax) }}, results will be accessible for up to
            {{ accessibleRespondents }} respondents.
          </p>
        </div>
      </div>

      <div
        v-if="exceedsContract && availableOnContract !== Infinity"
        class="rounded-md bg-status-warning-soft border border-status-warning-border px-3 py-2 text-xs text-status-warning-strong flex items-start gap-2">
        <TriangleAlert class="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          <p class="font-medium">Contract credit limit reached</p>
          <p>
            You've reached the contract limit ({{ availableOnContract }} {{ creditsLabel(availableOnContract) }} available).
            Contact your CSM to allocate more.
          </p>
        </div>
      </div>

      <div
        v-if="isAtConsumedFloor && consumedAutoEffective > 0"
        class="rounded-md bg-status-warning-soft border border-status-warning-border px-3 py-2 text-xs text-status-warning-strong flex items-start gap-2">
        <TriangleAlert class="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          <p class="font-medium">Consumed credits floor reached</p>
          <p>
            You can't allocate fewer credits than what has already been consumed ({{ consumedAutoEffective }} {{ creditsLabel(consumedAutoEffective) }}).
          </p>
        </div>
      </div>
    </template>
  </div>
</template>
